import React from "react";
import { StaticImage } from "gatsby-plugin-image";

import { MicroCombo } from "../components/Trustpilot/TrustpilotWidgets";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";

import "../../scss/main.scss";

const partnerBenefits = [
	{
		number: "01",
		title: "Protect continuity",
		copy:
			"Give families a considered next step when hourly visits are no longer enough, without leaving them to begin again alone.",
	},
	{
		number: "02",
		title: "Keep complementary visits",
		copy:
			"Your team can remain involved through double-up care, respite or additional visits where that is right for the client.",
	},
	{
		number: "03",
		title: "Create referral value",
		copy:
			"A clear commercial model recognises both a successful introduction and an ongoing collaborative role.",
	},
];

const commercialTerms = [
	{
		label: "Upfront referral fee",
		value: "£800",
		copy:
			"Paid when a client you refer starts care and remains with Edyn for four weeks.",
	},
	{
		label: "Collaborative care",
		value: "2% weekly",
		copy:
			"Available when your agency continues to support the client through double-up care, respite or additional visits.",
	},
	{
		label: "Typical total outcome",
		value: "~£1,296",
		copy:
			"Based on a £1,417 average weekly package and a 320-day average client duration.",
	},
];

const partnerSteps = [
	{
		number: "01",
		title: "Introduce the family",
		copy:
			"Share the situation with our partnership team and make a warm introduction when the family is ready.",
	},
	{
		number: "02",
		title: "We assess together",
		copy:
			"An Edyn Care Manager gets to know the person, their home, routines and support needs.",
	},
	{
		number: "03",
		title: "Care starts at home",
		copy:
			"We introduce suitable professional carers and manage the care plan, placement and ongoing quality oversight.",
	},
	{
		number: "04",
		title: "The partnership continues",
		copy:
			"Where appropriate, both teams stay connected around one clear plan as the client's needs change.",
	},
];

export default function PartnerWithUs() {
	return (
		<>
			<Seo
				title="Care Agency Partnerships | Edyn Care"
				description="Partner with Edyn to give clients a fully managed live-in care option when hourly visits are no longer enough."
				path="/partner-with-us/"
			/>

			<div className="container edyn-redesign-shell">
				<Header />
				<main id="main-content" className="edyn-partner-page">
					<section className="edyn-partner-hero">
						<div className="edyn-partner-hero__texture" aria-hidden="true" />
						<div className="edyn-partner-hero__grid site-shell">
							<div className="edyn-partner-hero__copy">
								<p className="eyebrow eyebrow--light">
									For hourly and visiting care providers
								</p>
								<h1>When care needs grow, the relationship can continue.</h1>
								<p className="edyn-partner-hero__lead">
									When hourly care is no longer enough, introduce a fully
									managed live-in option without leaving the client to navigate
									the next step alone.
								</p>
								<div className="edyn-partner-hero__actions">
									<a className="button button--cream" href="#partner-typeform">
										Register your agency
									</a>
									<a className="edyn-partner-hero__call" href="tel:02039709900">
										Call 020 3970 9900 <span aria-hidden="true">→</span>
									</a>
								</div>
								<MicroCombo
									classNames="edyn-partner-hero__trustpilot"
									theme="dark"
									alignment="left"
									width="280px"
								/>
							</div>

							<div className="edyn-partner-hero__media">
								<div className="edyn-partner-hero__halo" aria-hidden="true" />
								<StaticImage
									src="../../static/assets/marketing-site/images/partner-with-us/hero.jpg"
									alt="Older woman arranging flowers in her kitchen"
									className="edyn-partner-hero__image"
									objectFit="cover"
									objectPosition="center center"
									placeholder="blurred"
								/>
								<div className="edyn-partner-hero__note">
									<span
										className="edyn-partner-hero__note-mark"
										aria-hidden="true"
									>
										+
									</span>
									<p>
										<strong>Betty, at home.</strong>A familiar relationship,
										with more support around it.
									</p>
								</div>
							</div>
						</div>
					</section>

					<section className="edyn-partner-why section-space">
						<div className="site-shell edyn-partner-why__grid">
							<div className="edyn-partner-why__media">
								<div
									className="edyn-partner-why__backdrop"
									aria-hidden="true"
								/>
								<StaticImage
									src="../../static/assets/marketing-site/images/partner-with-us/partnership-2.jpg"
									alt="Professional carer supporting an older person at home"
									className="edyn-partner-why__image"
									objectFit="cover"
									objectPosition="center center"
									placeholder="blurred"
								/>
								<div className="edyn-partner-why__caption">
									<span aria-hidden="true">+</span>
									<p>
										<strong>Care that stays joined up.</strong>A warm
										introduction, with support around the whole family.
									</p>
								</div>
							</div>

							<div className="edyn-partner-why__copy">
								<p className="eyebrow">Why partner with edyn.care</p>
								<h2>Protect the relationship. Extend what is possible.</h2>
								<p className="edyn-partner-why__intro">
									When a client&apos;s needs move beyond visiting care, families
									need a clear next step from someone they already trust. Edyn
									helps you offer that route without undoing the relationship
									your team has built.
								</p>
								<div className="edyn-partner-why__reasons">
									<div className="edyn-partner-why__reason">
										<span>01</span>
										<div>
											<strong>A trusted next step</strong>
											<p>
												Offer a clear route when hourly care is no longer
												enough.
											</p>
										</div>
									</div>
									<div className="edyn-partner-why__reason">
										<span>02</span>
										<div>
											<strong>Care that stays connected</strong>
											<p>
												We work alongside the people who already know the
												client.
											</p>
										</div>
									</div>
									<div className="edyn-partner-why__reason">
										<span>03</span>
										<div>
											<strong>Your reputation protected</strong>
											<p>
												Families receive a fully managed service with clear
												communication.
											</p>
										</div>
									</div>
								</div>
								<a className="button button--primary" href="#partner-typeform">
									Talk to our partnership team
								</a>
							</div>
						</div>
					</section>

					<section
						className="edyn-partner-proof"
						aria-label="Partnership standards"
					>
						<div className="site-shell edyn-partner-proof__grid">
							<div>
								<strong>Fully managed</strong>
								<span>One accountable care plan</span>
							</div>
							<div>
								<strong>CQC regulated</strong>
								<span>Care delivered to regulated standards</span>
							</div>
							<div>
								<strong>Collaborative care</strong>
								<span>Your team can remain involved where appropriate</span>
							</div>
						</div>
					</section>

					<section className="edyn-partner-continuity section-space">
						<div className="site-shell edyn-partner-continuity__grid">
							<div className="edyn-partner-continuity__copy">
								<p className="eyebrow">Continuity, not a hand-off</p>
								<h2>A next step that keeps the client at home.</h2>
								<p>
									Edyn works alongside local providers so the client can remain
									at home, your agency stays involved where appropriate and the
									family has one clear plan around them.
								</p>
								<a className="button button--primary" href="#partner-typeform">
									Discuss a partnership
								</a>
							</div>

							<div
								className="edyn-partner-flow"
								aria-label="How services connect"
							>
								<div className="edyn-partner-flow__item">
									<span>01</span>
									<strong>Your relationship</strong>
									<p>Local knowledge and a trusted introduction.</p>
								</div>
								<div
									className="edyn-partner-flow__connector"
									aria-hidden="true"
								>
									+
								</div>
								<div className="edyn-partner-flow__item edyn-partner-flow__item--feature">
									<span>02</span>
									<strong>Edyn live-in care</strong>
									<p>A professional carer and fully managed support.</p>
								</div>
								<div
									className="edyn-partner-flow__connector"
									aria-hidden="true"
								>
									=
								</div>
								<div className="edyn-partner-flow__item">
									<span>03</span>
									<strong>One joined-up plan</strong>
									<p>Clear roles and continuity for the family.</p>
								</div>
							</div>
						</div>
					</section>

					<section className="edyn-partner-benefits section-space">
						<div className="edyn-partner-benefits__leaves" aria-hidden="true" />
						<div className="site-shell">
							<div className="section-heading">
								<p className="eyebrow">Built for both teams</p>
								<h2>A partnership that respects the work already done.</h2>
							</div>
							<div className="edyn-partner-benefits__grid">
								{partnerBenefits.map((benefit) => (
									<article key={benefit.number}>
										<span>{benefit.number}</span>
										<h3>{benefit.title}</h3>
										<p>{benefit.copy}</p>
									</article>
								))}
							</div>
						</div>
					</section>

					<section className="edyn-partner-commercial section-space">
						<div
							className="edyn-partner-commercial__rings"
							aria-hidden="true"
						/>
						<div className="site-shell">
							<div className="edyn-partner-commercial__intro">
								<div>
									<p className="eyebrow eyebrow--light">
										A clear commercial model
									</p>
									<h2>
										Value at introduction. Value when care stays connected.
									</h2>
								</div>
								<p>
									The model recognises a successful referral and, where your
									agency remains part of the care arrangement, the ongoing work
									your team provides.
								</p>
							</div>
							<div className="edyn-partner-commercial__grid">
								{commercialTerms.map((term) => (
									<article key={term.label}>
										<p className="edyn-partner-commercial__label">
											{term.label}
										</p>
										<p className="edyn-partner-commercial__value">
											{term.value}
										</p>
										<p>{term.copy}</p>
									</article>
								))}
							</div>
							<p className="edyn-partner-commercial__footnote">
								Based on average package duration and cost. Additional services
								such as driver support or food allowances are not included.
							</p>
						</div>
					</section>

					<section className="edyn-partner-steps section-space">
						<div className="site-shell">
							<div className="section-heading">
								<p className="eyebrow">How the partnership works</p>
								<h2>A warm introduction, followed through properly.</h2>
							</div>
							<ol className="edyn-partner-steps__grid">
								{partnerSteps.map((step) => (
									<li key={step.number}>
										<span>{step.number}</span>
										<h3>{step.title}</h3>
										<p>{step.copy}</p>
									</li>
								))}
							</ol>
						</div>
					</section>

					<section className="edyn-partner-trust section-space">
						<div className="site-shell edyn-partner-trust__grid">
							<div className="edyn-partner-trust__media">
								<StaticImage
									src="../../static/assets/marketing-site/images/partner-with-us/partnership-3.jpg"
									alt="Care professional supporting an older person at home"
									className="edyn-partner-trust__image"
									objectFit="cover"
									placeholder="blurred"
								/>
							</div>
							<div className="edyn-partner-trust__copy">
								<p className="eyebrow">Confidence in the introduction</p>
								<h2>
									A live-in care partner you can introduce with confidence.
								</h2>
								<p>
									Edyn takes responsibility for assessment, carer selection,
									care planning, coordination and ongoing oversight, while
									keeping the family informed throughout.
								</p>
								<MicroCombo
									classNames="edyn-partner-trust__trustpilot"
									alignment="left"
									width="300px"
								/>
							</div>
						</div>
					</section>

					<section
						className="edyn-partner-form section-space"
						id="partner-typeform"
					>
						<div className="edyn-partner-form__texture" aria-hidden="true" />
						<div className="site-shell edyn-partner-form__grid">
							<div className="edyn-partner-form__copy">
								<p className="eyebrow">For care agencies</p>
								<h2>Start a partnership conversation.</h2>
								<p>
									Tell us about your agency and the services you provide. Our
									partnership team will come back to you to discuss how a
									referral and collaborative-care relationship could work.
								</p>
								<a href="tel:02039709900">
									Prefer to speak? Call 020 3970 9900
									<span aria-hidden="true"> →</span>
								</a>
							</div>
							<div className="edyn-partner-form__embed">
								<iframe
									src="https://edyncare.typeform.com/to/lDhGUk01"
									title="Register your care agency with Edyn"
									loading="lazy"
									allow="camera; microphone; autoplay; encrypted-media;"
								/>
							</div>
						</div>
					</section>
				</main>
				<Footer includeCTA={false} />
			</div>
		</>
	);
}
